import React, { useState, useRef, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Avatar,
  message,
  Progress,
  AutoComplete,
  Tooltip,
} from 'antd';
import { CachePersistor } from 'apollo3-cache-persist';
import { useHistory } from 'react-router-dom';

import PageHeader from 'components/PageHeader';
import TOTPModal from 'components/TOTPModal';
import Loader from 'components/Loader';
import Uploader from 'components/Uploader';
import helpers from 'helpers';
import Path from 'routes/path';
import Popconfirm from 'components/PopConfirm';
import CountriesCode from 'constants/countries_code';
import InfoIcon from 'components/Icons/InfoIcon';
import TFAShieldIcon from 'components/Icons/TFAShieldIcon';
import TFAShieldCancelIcon from 'components/Icons/TFAShieldCancelIcon';
import SettingsResetIcon from 'components/Icons/SettingsResetIcon';
import { LoadingOutlined } from '@ant-design/icons';

import { getUserShortName } from 'helpers/users';
import utils from 'utils/utils';
import { resizeImage } from 'utils/image';
import { USER_DETAILS } from 'graphql/queries/userQueries';
import {
  USER_DETAILS_UPDATE,
  USER_PASSWORD_UPDATE,
  USER_DELETE,
  USER_GENERATE_TOTP,
  USER_TOGGLE_2FA,
} from 'graphql/mutations/userMutations';
import { useLazyQuery, useMutation, useApolloClient, useReactiveVar } from '@apollo/client';
import { cache, getUserId, userVar, isLoggedInVar, userProfileVar } from 'graphql/cache';

import './Profile.scss';

const FormItem = ({
  placeholder,
  label,
  value,
  handleInputChange,
  validateStatus,
  help,
  colon,
  type,
  name,
  disabled,
  maxLength,
}) => (
  <Col md={12} lg={12} xl={12} className="input-container">
    <Form.Item
      label={label}
      colon={colon || true}
      validateStatus={validateStatus ? 'error' : ''}
      help={help || ''}>
      <Input
        placeholder={placeholder || ''}
        value={value}
        name={name}
        onChange={handleInputChange}
        type={type || 'text'}
        disabled={disabled}
        maxLength={maxLength}
      />
    </Form.Item>
  </Col>
);

const Profile = () => {
  const [upload, uploadStatus] = Uploader();
  const [saving2FA, setSaving2FA] = useState(false);
  const [emailValidateError, setEmailValidateError] = useState('');
  const [numberValidateError, setNumberValidateError] = useState('');
  const [whatsappNumberValidateError, setWhatsappNumberValidateError] = useState('');
  const [nameValidateError, setNameValidateError] = useState('');
  const [companyNameValidateError, setCompanyNameValidateError] = useState('');
  const [showTOTPModal, setShowTOTPModal] = useState(false);
  const [popConfirmDetails, setPopConfirmDetails] = useState({
    show: false,
  });

  const inputFileRef = useRef(null);
  const history = useHistory();
  const client = useApolloClient();
  const user = useReactiveVar(userVar);

  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState([]);
  const [saving, setSaving] = useState(false);

  const profileData = user
    ? {
        ...user?.profile,
        email: user.emails[0].address,
        role: user?.roles && user?.roles[0],
      }
    : {
        name: '',
        email: '',
        companyname: '',
        address: '',
        country: '',
        countryCode: '',
        vatnumber: '',
        image: '',
        contactnumber: '',
        role: '',
        whatsappNumber: '',
        enable2FA: '',
      };
  const [originalProfile, setOriginalProfile] = useState({ ...profileData });
  const [profile, setProfile] = useState({ ...profileData });

  const [QrUrl, setQrUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [recoveryToken, setRecoveryToken] = useState('');
  const [showPasswordEdit, setShowPasswordEdit] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordValidationError, setPasswordValidationError] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordStrengthError, setPasswordStrengthError] = useState('');

  const isSettingsDisabled = profile.role === 'editor' || profile.role === 'manager' ? true : false;

  const [loadUser] = useLazyQuery(USER_DETAILS, {
    variables: { id: getUserId() },
    fetchPolicy: 'network-only',
    onCompleted: (res) => {
      const {
        fetchUser: {
          profile: {
            companyname,
            name,
            address,
            country,
            countryCode,
            image,
            vatnumber,
            contactnumber,
            whatsappNumber,
            enable2FA,
          },
          emails,
          roles,
        },
      } = res;

      setOptions([{ label: country, value: country }]);

      const profileData = {
        ...profile,
        name,
        companyname,
        email: emails[0].address,
        address,
        country,
        countryCode,
        image,
        contactnumber,
        vatnumber,
        role: roles && roles[0],
        whatsappNumber,
        enable2FA,
      };

      setProfile({ ...profileData });
      setOriginalProfile({ ...profileData });
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const [saveProfile] = useMutation(USER_DETAILS_UPDATE, {
    errorPolicy: 'none',
    async onCompleted() {
      cache.evict('fetchUser');
      userProfileVar(profile);
      setEmailValidateError('');
      setNumberValidateError('');
      setWhatsappNumberValidateError('');
      message.success('Profile updated successfully');
      setSaving(false);
    },
    onError: ({ graphQLErrors }) => {
      setSaving(false);
      message.error(graphQLErrors?.[0]?.message);
    },
  });

  const [toggle2FA] = useMutation(USER_TOGGLE_2FA, {
    errorPolicy: 'none',
    async onCompleted({ toggle2FA }) {
      cache.evict('fetchUser');
      const { profile: updatedProfile } = toggle2FA;
      const data = {
        ...profile,
        enable2FA: updatedProfile.enable2FA,
      };
      userProfileVar(data);
      setProfile({ ...data });
      setOriginalProfile({ ...data });
      setSaving2FA(false);
      setShowTOTPModal(false);
      message.success(
        `${updatedProfile.enable2FA ? '2FA enabled successfully' : '2FA disabled successfully'}`
      );
    },
    onError: ({ graphQLErrors }) => {
      setSaving2FA(false);
      message.error(graphQLErrors?.[0]?.message);
    },
  });

  const [deleteUser, { loading: deleteUserLoading }] = useMutation(USER_DELETE, {
    errorPolicy: 'none',
    async onCompleted() {
      const persistor = new CachePersistor({
        cache: client.cache,
        storage: window.localStorage,
      });

      await client.clearStore();
      await persistor.purge();

      isLoggedInVar(false);
      userProfileVar('');
      userVar('');

      localStorage.clear();
      sessionStorage.removeItem('lastTimeStamp');
      history.push(Path.LOGIN);
    },
    onError: ({ graphQLErrors }) => {
      message.error(graphQLErrors[0].message);
    },
  });

  const [updatePassword] = useMutation(USER_PASSWORD_UPDATE, {
    onCompleted() {
      setShowPasswordEdit(false);
      message.success('Password changed successfully');
    },
    onError: ({ graphQLErrors }) => {
      message.error(graphQLErrors[0].message);
    },
  });

  const [generateTOTP, { loading: loadingTOTP }] = useMutation(USER_GENERATE_TOTP, {
    onCompleted: (data) => {
      setShowTOTPModal(true);
    },
    onError: (error) => {
      message.error('Error Generating Secret ' + error.message);
    },
  });

  const onGenerateTOTPClick = async () => {
    if (profile.enable2FA) {
      handleUpdate2FA(false);
    } else {
      try {
        const { data } = await generateTOTP({ variables: { userId: getUserId() } });
        const { manualKey, qrCodeUrl, recoveryToken } = data.generateTOTP;
        setRecoveryToken(recoveryToken);
        setManualKey(manualKey);
        setQrUrl(qrCodeUrl);
      } catch (error) {
        message.error(error.message);
      }
    }
  };

  useEffect(() => {
    setLoading(true);
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.title = 'Prospero - Profile';
  });

  if (loading) {
    return <Loader />;
  }

  const uploadUserPhoto = () => {
    inputFileRef.current.click();
  };

  const onImageSelect = async ({
    target: {
      validity,
      files: [file],
    },
  }) => {
    if (validity.valid) {
      try {
        const reSizedImage = await resizeImage({
          file: file,
          path: 'profile',
          maxWidth: 200,
          maxHeight: 200,
        });

        const { url } = await upload(reSizedImage, `profile/${user._id}`);
        setProfile({
          ...profile,
          image: url,
        });
      } catch (error) {
        message.error(error.message);
      }
    }
  };

  const handleInputChange = (e) => {
    if (emailValidateError && e.target.name === 'email') {
      setEmailValidateError('');
    }
    if (nameValidateError && e.target.name === 'name') {
      setNameValidateError('');
    }
    if (companyNameValidateError && e.target.name === 'companyname') {
      setCompanyNameValidateError('');
    }

    const newProfile = { ...profile };
    newProfile[e.target.name] = e.target.value;
    setProfile(newProfile);
  };

  const onSearch = (searchText) => {
    let newOptions = [];

    !searchText
      ? []
      : Object.keys(CountriesCode).forEach((countryCode) => {
          if (CountriesCode[countryCode].name.search(new RegExp(searchText, 'i')) !== -1)
            newOptions.push({
              label: CountriesCode[countryCode].name,
              value: CountriesCode[countryCode].name,
            });
        });

    setOptions(newOptions);
  };

  const handleUpdate2FA = (value) => {
    // Remove cookie on update
    const skipCookie = utils.readCookie(`skipTOTP_${user?._id}`);
    if (skipCookie) {
      utils.deleteCookie(`skipTOTP_${user?._id}`);
    }

    setSaving2FA(true);
    toggle2FA({
      variables: {
        userId: getUserId(),
        value,
        recoveryToken,
        twoFASecret: manualKey,
      },
    });
  };

  const handleSave = () => {
    let error = false;
    let newProfile = { ...profile };

    if (!newProfile.name.trim()) {
      setNameValidateError('Name cannot be empty');
      error = true;
    } else if (newProfile.name.length > 100) {
      setNameValidateError('Name cannot be greater than 100 characters');
      error = true;
    }

    if (!newProfile.companyname.trim()) {
      setCompanyNameValidateError('Company name cannot be empty or just spaces');
      error = true;
    } else if (newProfile.companyname.length > 120) {
      setCompanyNameValidateError('Company name cannot be greater than 120 characters');
      error = true;
    }

    if (!newProfile.email) {
      setEmailValidateError('Email cannot be empty');
      error = true;
    } else if (!helpers.validateEmail(newProfile.email)) {
      setEmailValidateError('Invalid email');
      error = true;
    }
    /* eslint-disable */
    if (
      !!newProfile.whatsappNumber &&
      !/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g.test(newProfile.whatsappNumber)
    ) {
      error = true;
      setWhatsappNumberValidateError('Invalid whatsapp number');
    }

    if (
      !!newProfile.contactnumber &&
      !/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g.test(newProfile.contactnumber)
    ) {
      error = true;
      setNumberValidateError('Invalid contact number');
    }

    if (
      !!newProfile.email &&
      !/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        newProfile.email
      )
    ) {
      error = true;
      setEmailValidateError('Invalid email');
    }
    /* eslint-enable */

    newProfile['name'] = newProfile.name.trim();
    newProfile['companyname'] = newProfile.companyname.trim();

    let countryCode = newProfile.countryCode || newProfile.country;

    Object.keys(CountriesCode).forEach((code) => {
      if (CountriesCode[code]?.name === newProfile.country) countryCode = code;
    });

    newProfile['countryCode'] = countryCode;

    if (isSettingsDisabled) {
      newProfile = {
        ...user.profile,
        email: newProfile?.email,
        name: newProfile?.name,
        contactnumber: newProfile?.contactnumber,
        image: newProfile?.image,
      };
    }

    if (!error) {
      setSaving(true);
      saveProfile({
        variables: {
          id: getUserId(),
          profile: newProfile,
        },
      });
    }
  };

  const handlePasswordChange = (e) => {
    const newPasswordForm = { ...passwordForm };
    newPasswordForm[e.target.name] = e.target.value;
    if (passwordValidationError[e.target.name]) {
      passwordValidationError[e.target.name] = '';
      setPasswordValidationError({ ...passwordValidationError });
    }
    setPasswordForm(newPasswordForm);

    if (e.target.name !== 'oldPassword') {
      const errorResponse = utils.simplePasswordPolicy(e.target.value);
      if (errorResponse !== null) {
        setPasswordStrengthError(errorResponse);
        passwordValidationError[e.target.name] = errorResponse;
      }
    }
  };

  const submitPassword = () => {
    let error = false;

    setPasswordValidationError({
      confirmPassword: '',
      newPassword: '',
      oldPassword: '',
    });

    const newPasswordValidationError = {
      confirmPassword: '',
      newPassword: '',
      oldPassword: '',
    };

    if (!passwordForm.oldPassword) {
      newPasswordValidationError.oldPassword = 'This field is required';
      error = true;
    }

    if (!passwordForm.newPassword) {
      newPasswordValidationError.newPassword = 'This field is required';
      error = true;
    } else if (passwordForm.newPassword.length < 8) {
      newPasswordValidationError.newPassword =
        'The password should have minimum of 8 character length';
      error = true;
    }

    if (!passwordForm.confirmPassword) {
      newPasswordValidationError.confirmPassword = 'This field is required';
      error = true;
    } else if (passwordForm.confirmPassword !== passwordForm.newPassword) {
      newPasswordValidationError.confirmPassword = 'The passwords should be same';
      error = true;
    }

    setPasswordValidationError(newPasswordValidationError);

    if (!error) {
      updatePassword({
        variables: passwordForm,
      });
    }
  };

  const resetPasswordForm = () => {
    setPasswordValidationError({
      confirmPassword: '',
      newPassword: '',
      oldPassword: '',
    });
    setPasswordForm({
      confirmPassword: '',
      newPassword: '',
      oldPassword: '',
    });
  };

  const resetProfileForm = () => {
    setProfile({ ...originalProfile });
  };

  const deleteUserProfile = () => {
    setPopConfirmDetails((prevState) => ({
      ...prevState,
      show: true,
      title: 'Delete Profile?',
      body: 'All your account data, including your proposals, support interactions and usage data will be lost forever. There is no way to undo this step.',
      cancelText: 'CANCEL',
      confirmText: 'YES, DELETE',
      onConfirm: deleteUser,
    }));
  };

  return (
    <div className="container narrow-container">
      <div className="profile">
        <Row className="profile-header">
          <PageHeader pageTitle="Profile" showButton={false} />
        </Row>
        <Form className="profile-form" layout="vertical">
          <div>
            <Row className="content">
              <Col xs={24} className="input-container">
                <Form.Item label="User Photo" colon={false} help="">
                  {uploadStatus.status === 'uploading' ? (
                    <Progress type="circle" percent={uploadStatus.percent} />
                  ) : (
                    <div>
                      {profile.image ? (
                        <Avatar src={profile.image} size={64} />
                      ) : (
                        <Avatar size={64}>{getUserShortName({ name: profile.name })}</Avatar>
                      )}
                      <Button
                        className="change-btn small-btn grey-btn"
                        size="small"
                        onClick={uploadUserPhoto}>
                        Change
                      </Button>
                    </div>
                  )}
                </Form.Item>
              </Col>
              <FormItem
                value={profile.name}
                placeholder="Write your full name"
                name="name"
                label="Full Name"
                validateStatus={nameValidateError}
                help={nameValidateError}
                handleInputChange={handleInputChange}
              />
              <FormItem
                value={profile.email}
                placeholder="Write your email address"
                name="email"
                label="Email"
                validateStatus={emailValidateError}
                help={emailValidateError}
                handleInputChange={handleInputChange}
                colon={false}
              />
              <FormItem
                value={profile.contactnumber}
                placeholder="Write your contact number e.g. +12163547758"
                name="contactnumber"
                label={
                  <>
                    <span>Contact number</span>
                    <Tooltip
                      title={
                        <>
                          Make sure to include the country code in the mobile number for phone
                          calls.
                        </>
                      }
                      color="white">
                      <InfoIcon />
                    </Tooltip>
                  </>
                }
                handleInputChange={handleInputChange}
                validateStatus={numberValidateError}
                help={numberValidateError}
                colon={false}
              />
              <FormItem
                value={profile.companyname}
                placeholder="Write your company name"
                name="companyname"
                label="Company name (appears on your invoice)"
                handleInputChange={handleInputChange}
                colon={false}
                validateStatus={companyNameValidateError}
                help={companyNameValidateError}
                disabled={isSettingsDisabled}
              />
              <FormItem
                value={profile.address}
                placeholder="Write your address"
                name="address"
                label="Address"
                handleInputChange={handleInputChange}
                colon={false}
                help=""
                disabled={isSettingsDisabled}
              />
              <Col md={12} lg={12} xl={12} className="input-container">
                <Form.Item label="Country" name="country" colon={false}>
                  <AutoComplete
                    defaultValue={profile.country}
                    options={options}
                    onSearch={onSearch}
                    onChange={(countryValue) =>
                      handleInputChange({ target: { name: 'country', value: countryValue } })
                    }
                    disabled={isSettingsDisabled}
                  />
                </Form.Item>
              </Col>
              <FormItem
                value={profile.vatnumber}
                placeholder="Write your VAT / Business number"
                name="vatnumber"
                label="VAT / Business number"
                handleInputChange={handleInputChange}
                colon={false}
                help=""
                disabled={isSettingsDisabled}
              />
              <FormItem
                value={profile.whatsappNumber}
                placeholder="Write your WhatsApp Number e.g. +12163547758"
                name="whatsappNumber"
                label={
                  <>
                    <span>WhatsApp number</span>
                    <Tooltip
                      title={
                        <>
                          Make sure to include the country code in the mobile number for WhatsApp
                          messages.
                        </>
                      }
                      color="white">
                      <InfoIcon />
                    </Tooltip>
                  </>
                }
                validateStatus={whatsappNumberValidateError}
                help={whatsappNumberValidateError}
                handleInputChange={handleInputChange}
                colon={false}
                disabled={isSettingsDisabled}
              />
            </Row>
            {showPasswordEdit ? (
              <React.Fragment>
                <Row className="divider">&nbsp;</Row>
                <Row className="password-form content">
                  <FormItem
                    value={passwordForm.oldPassword}
                    name="oldPassword"
                    label="Enter your old password"
                    handleInputChange={handlePasswordChange}
                    validateStatus={passwordValidationError.oldPassword ? 'error' : ''}
                    help={passwordValidationError.oldPassword}
                    colon={false}
                    type="password"
                  />

                  <FormItem
                    value={passwordForm.newPassword}
                    name="newPassword"
                    label="Enter new password"
                    handleInputChange={handlePasswordChange}
                    validateStatus={passwordValidationError.newPassword ? 'error' : ''}
                    help={passwordValidationError.newPassword}
                    colon={false}
                    type="password"
                    onInput={passwordStrengthError}
                  />

                  <FormItem
                    value={passwordForm.confirmPassword}
                    name="confirmPassword"
                    label="Confirm password"
                    handleInputChange={handlePasswordChange}
                    colon={false}
                    validateStatus={passwordValidationError.confirmPassword ? 'error' : ''}
                    help={passwordValidationError.confirmPassword}
                    type="password"
                    onInput={passwordStrengthError}
                  />
                </Row>
                <Row className="password-actions">
                  <Button
                    className="link-btn"
                    type="link"
                    onClick={() => {
                      setShowPasswordEdit(true);
                      submitPassword();
                    }}>
                    Submit password
                  </Button>
                  <Button
                    className="link-btn cancel-btn"
                    type="link"
                    onClick={() => {
                      setShowPasswordEdit(false);
                      resetPasswordForm();
                    }}>
                    Cancel
                  </Button>
                </Row>
              </React.Fragment>
            ) : (
              <Row className="content change-password">
                <div className="ant-form-item">
                  <Button
                    className="change-password link-btn"
                    type="link"
                    onClick={() => {
                      setShowPasswordEdit(true);
                    }}>
                    Change password
                  </Button>
                </div>
                <div className="ant-form-item">
                  <Button
                    className="tfa-enable-btn link-btn"
                    type="link"
                    onClick={onGenerateTOTPClick}>
                    <span className="shield-icon">
                      {profile.enable2FA ? <TFAShieldCancelIcon /> : <TFAShieldIcon />}
                    </span>
                    {profile.enable2FA ? (
                      <span className="prosper-button-text error">Disable 2FA</span>
                    ) : (
                      <span className="prosper-button-text">Enable 2FA</span>
                    )}
                    {saving2FA || loadingTOTP ? <LoadingOutlined /> : ''}
                  </Button>
                </div>
              </Row>
            )}
          </div>
          <div className="profile-actions">
            <Button
              className="large-btn secondary-btn save-btn"
              type="primary"
              loading={saving}
              onClick={handleSave}>
              SAVE
            </Button>
            <Button
              className="large-btn transparent-btn reset-btn hover"
              type="link"
              icon={<SettingsResetIcon />}
              onClick={resetProfileForm}>
              RESET
            </Button>
            <Button className="large-btn grey-btn delete-btn" onClick={deleteUserProfile}>
              DELETE PROFILE
            </Button>
          </div>
        </Form>
      </div>
      <input
        type="file"
        className="file_bag"
        accept="image/*"
        ref={inputFileRef}
        onChange={onImageSelect}
        style={{ display: 'none', position: 'absolute', left: -10000 }}
      />
      {popConfirmDetails.show && (
        <Popconfirm
          title={popConfirmDetails.title}
          body={popConfirmDetails.body}
          visible={popConfirmDetails.show}
          cancelText={popConfirmDetails.cancelText}
          confirmText={popConfirmDetails.confirmText}
          modalClassName={popConfirmDetails.className}
          onCancel={() =>
            setPopConfirmDetails((prevState) => ({
              ...prevState,
              show: false,
            }))
          }
          onConfirm={popConfirmDetails.onConfirm}
          okButtonProps={{ loading: deleteUserLoading }}
        />
      )}
      {showTOTPModal && (
        <TOTPModal
          visible={showTOTPModal}
          enabling2FA={saving2FA}
          onCancel={() => setShowTOTPModal(false)}
          onConfirm={handleUpdate2FA}
          manualKey={manualKey}
          QrUrl={QrUrl}
          recoveryToken={recoveryToken}
        />
      )}
    </div>
  );
};

Profile.propTypes = {};

export default Profile;
